import { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, Polyline, CircleMarker, useMap } from "react-leaflet";
import { toast } from "sonner";
import { MapPin, Clock, Route as RouteIcon, Timer, Navigation, AlertTriangle } from "lucide-react";
import { ltApi, fmtTime, fmtDate, fmtHours, fmtDistance, presetRange, istToday, endpointIcon, TILE_URL, TILE_ATTRIBUTION, DEFAULT_CENTER } from "./ltShared";

const PRESETS = [["today", "Today"], ["yesterday", "Yesterday"], ["week", "This Week"], ["month", "This Month"], ["custom", "Custom"]];

function FitRoute({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points.length) return;
    if (points.length === 1) map.setView(points[0], 16);
    else map.fitBounds(points, { padding: [36, 36], maxZoom: 17 });
  }, [points, map]);
  return null;
}

export default function RouteTab({ businessId, employees, initialUserId }) {
  const [userId, setUserId] = useState(initialUserId || "");
  const [preset, setPreset] = useState("today");
  const [custom, setCustom] = useState({ from: istToday(), to: istToday() });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sel, setSel] = useState(0);

  useEffect(() => { if (initialUserId) setUserId(initialUserId); }, [initialUserId]);
  const range = preset === "custom" ? custom : presetRange(preset);

  useEffect(() => {
    if (!userId) { setData(null); return; }
    setLoading(true);
    ltApi("/route", { businessId, params: { user_id: userId, from: range.from, to: range.to } })
      .then((d) => { setData(d); setSel(Math.max(0, d.sessions.length - 1)); })
      .catch((e) => toast.error(e.message))
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [userId, businessId, range.from, range.to]);

  const session = data && data.sessions[sel];
  const pts = useMemo(() => (session ? session.points : []), [session]);
  const line = useMemo(() => pts.map((p) => [p.latitude, p.longitude]), [pts]);
  const first = pts[0], last = pts[pts.length - 1];
  const ended = session && session.attendance.punch_out_time;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="min-w-[220px] flex-1">
          <label className="text-xs font-medium text-slate-600">Employee</label>
          <select value={userId} onChange={(e) => setUserId(e.target.value)} data-testid="lt-route-employee" className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-3 text-sm">
            <option value="">Select employee…</option>
            {employees.map((e) => <option key={e.user_id} value={e.user_id}>{e.name}{e.employee_id ? ` (${e.employee_id})` : ""}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600">Period</label>
          <div className="mt-1 flex flex-wrap gap-1">
            {PRESETS.map(([k, l]) => (
              <button key={k} onClick={() => setPreset(k)} className={`h-10 rounded-lg px-3 text-sm font-medium ${preset === k ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{l}</button>
            ))}
          </div>
        </div>
        {preset === "custom" && (
          <div className="flex items-end gap-2">
            <div><label className="text-xs font-medium text-slate-600">From</label><input type="date" value={custom.from} max={custom.to} onChange={(e) => setCustom((c) => ({ ...c, from: e.target.value }))} className="mt-1 h-10 rounded-lg border border-slate-300 px-2 text-sm" /></div>
            <div><label className="text-xs font-medium text-slate-600">To</label><input type="date" value={custom.to} min={custom.from} max={istToday()} onChange={(e) => setCustom((c) => ({ ...c, to: e.target.value }))} className="mt-1 h-10 rounded-lg border border-slate-300 px-2 text-sm" /></div>
          </div>
        )}
      </div>

      {!userId ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white py-16 text-center text-sm text-slate-500">Select an employee to view their route.</div>
      ) : loading && !data ? (
        <div className="py-16 text-center text-sm text-slate-400">Loading route…</div>
      ) : data && data.sessions.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white py-16 text-center text-sm text-slate-500">No work sessions in this period.</div>
      ) : data && (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
            {[
              [Clock, "Punch In", fmtTime(session?.attendance.punch_in_time)],
              [Clock, "Punch Out", ended ? fmtTime(session.attendance.punch_out_time) : "Working"],
              [Timer, "Working Hours", ended ? fmtHours(session.attendance.working_hours) : fmtHours((Date.now() - new Date(session.attendance.punch_in_time)) / 3600000)],
              [MapPin, "Location Updates", session?.point_count ?? 0],
              [Navigation, "Distance Travelled", fmtDistance(session?.distance_m)],
            ].map(([Icon, label, val]) => (
              <div key={label} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex items-center gap-1.5 text-xs font-medium text-slate-500"><Icon className="h-3.5 w-3.5" />{label}</div>
                <div className="mt-1 text-lg font-semibold text-slate-900">{val}</div>
              </div>
            ))}
          </div>

          <div className="grid gap-4 lg:grid-cols-[1fr_300px]">
            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
              <MapContainer center={DEFAULT_CENTER} zoom={5} style={{ height: 520, width: "100%" }} scrollWheelZoom>
                <TileLayer url={TILE_URL} attribution={TILE_ATTRIBUTION} />
                <FitRoute points={line} />
                {line.length > 1 && <Polyline positions={line} pathOptions={{ color: "#4f46e5", weight: 4, opacity: 0.85 }} />}
                {pts.slice(1, -1).map((p) => (
                  <CircleMarker key={p.location_id} center={[p.latitude, p.longitude]} radius={4} pathOptions={{ color: p.is_mock ? "#d97706" : "#4f46e5", fillColor: "#fff", fillOpacity: 1, weight: 2 }}>
                    <Popup>{fmtTime(p.captured_at)} · ±{p.accuracy != null ? Math.round(p.accuracy) : "?"} m{p.battery_level != null ? ` · ${p.battery_level}%` : ""}{p.is_mock ? " · possible mock location" : ""}</Popup>
                  </CircleMarker>
                ))}
                {first && <Marker position={[first.latitude, first.longitude]} icon={endpointIcon("IN", "#16a34a")}><Popup><b>Punch In</b> · {fmtTime(first.captured_at)}</Popup></Marker>}
                {last && last !== first && <Marker position={[last.latitude, last.longitude]} icon={endpointIcon(ended ? "OUT" : "•", ended ? "#dc2626" : "#4f46e5")}><Popup><b>{ended ? "Punch Out" : "Latest location"}</b> · {fmtTime(last.captured_at)}</Popup></Marker>}
              </MapContainer>
              {pts.length === 0 && <div className="border-t border-slate-100 px-4 py-2 text-xs text-slate-500">No GPS points were received for this session (e.g. punched in from the web).</div>}
            </div>

            <div className="flex max-h-[580px] flex-col rounded-2xl border border-slate-200 bg-white shadow-sm">
              {data.sessions.length > 1 && (
                <div className="border-b border-slate-100 p-3">
                  <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-400">Sessions ({data.sessions.length})</div>
                  <div className="flex max-h-40 flex-col gap-1 overflow-y-auto">
                    {data.sessions.map((s, i) => (
                      <button key={s.attendance.attendance_id} onClick={() => setSel(i)} className={`flex items-center justify-between rounded-lg px-2 py-1.5 text-left text-xs ${sel === i ? "bg-indigo-50 text-indigo-700" : "hover:bg-slate-50 text-slate-700"}`}>
                        <span className="font-medium">{fmtDate(s.attendance.date)}</span>
                        <span>{fmtDistance(s.distance_m)} · {s.point_count} pts</span>
                      </button>
                    ))}
                  </div>
                  <div className="mt-2 text-[11px] text-slate-500">Total: {fmtDistance(data.totals.distance_m)} · {fmtHours(data.totals.working_hours)} · {data.totals.points} updates</div>
                </div>
              )}
              <div className="px-3 pt-3 text-xs font-semibold uppercase tracking-wide text-slate-400">Timeline</div>
              <div className="flex-1 overflow-y-auto p-3">
                <ol className="relative ml-2 border-l-2 border-indigo-100">
                  {pts.map((p) => (
                    <li key={p.location_id} className="mb-2 ml-3">
                      <span className={`absolute -left-[7px] mt-1 h-3 w-3 rounded-full border-2 border-white ${p.kind === "punch_in" ? "bg-emerald-600" : p.kind === "punch_out" ? "bg-rose-600" : p.is_mock ? "bg-amber-500" : "bg-indigo-500"}`} />
                      <div className="text-xs font-medium text-slate-800">{p.kind === "punch_in" ? "Punch In" : p.kind === "punch_out" ? "Punch Out" : fmtTime(p.captured_at)}{p.kind !== "track" && <span className="font-normal text-slate-500"> · {fmtTime(p.captured_at)}</span>}</div>
                      <div className="text-[11px] text-slate-500">{p.latitude.toFixed(5)}, {p.longitude.toFixed(5)} · ±{p.accuracy != null ? Math.round(p.accuracy) : "?"} m{p.battery_level != null ? ` · ${p.battery_level}%` : ""}</div>
                      {p.is_mock && <div className="inline-flex items-center gap-1 text-[11px] text-amber-700"><AlertTriangle className="h-3 w-3" />Possible mock location</div>}
                    </li>
                  ))}
                </ol>
                {session?.point_count > pts.length && <div className="text-[11px] text-slate-400">Showing {pts.length} of {session.point_count} points (thinned for display; distance uses all points).</div>}
              </div>
            </div>
          </div>
          <p className="flex items-center gap-1.5 text-xs text-slate-400"><RouteIcon className="h-3.5 w-3.5" />Route is drawn only from GPS points the device actually reported. Low-accuracy fixes (&gt; 100 m) and impossible jumps are excluded from distance.</p>
        </>
      )}
    </div>
  );
}
